import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/ui.css';

import Navbar from './components/Navbar';
import MobileBottomNav from './components/Navigation/MobileBottomNav';
import ScreenerResults from './components/ScreenerResults';
import Watchlist from './components/Watchlist';
import StockAnalyzerDashboard from './components/StockAnalyzer/StockAnalyzerDashboard';
import AdminTestDashboard from './components/AdminTests/AdminTestDashboard';
import ResearchDashboard from './components/Research/ResearchDashboard';
import ScoringBuilderPage from './components/ScoringBuilder/ScoringBuilderPage';
import DashboardBuilder from './components/DashboardBuilder/DashboardBuilder';
import TickerDashboard from './components/DashboardBuilder/TickerDashboard';
import OptionsDashboard from './components/DashboardBuilder/OptionsDashboard';
import TestMACDChart from './components/DashboardBuilder/TestMACDChart';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import RequestPasswordResetForm from './components/RequestPasswordResetForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import { ProfilePage } from './components/Profile';
import UsageDashboard from './components/Profile/UsageDashboard';
import AffiliatePage from './components/Affiliate/AffiliatePage';
import {
  PricingPage,
  SubscriptionManagement,
  SubscriptionSuccessPage,
  SubscriptionCancelPage,
} from './components/Subscription';
import { logout } from './services/api';
import { CustomIndicatorProvider } from './contexts/CustomIndicatorContext';
import { LastClickedTickerProvider } from './contexts/LastClickedTickerContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { TutorialVideoProvider } from './contexts/TutorialVideoContext';
import FeatureGate from './components/FeatureGate';

const AUTH_PATHS = ['/login', '/signup', '/request-password-reset', '/reset-password'];

function NavbarWrapper({ isLoggedIn }) {
  const location = useLocation();
  const isAuthPage = AUTH_PATHS.some(path => location.pathname.startsWith(path));
  if (!isLoggedIn || isAuthPage) return null;
  return <Navbar />;
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useUser();
  if (loading) return null;
  return isAdmin ? children : <Navigate to="/" />;
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  return (
    <UserProvider>
      <TutorialVideoProvider>
      <CustomIndicatorProvider>
        <LastClickedTickerProvider>
          <Router>
            <div className="App dark bg-background text-text min-h-screen pb-[80px] md:pb-0">
              <NavbarWrapper isLoggedIn={isLoggedIn} />
              <MobileBottomNav />
              <div className="container-wide px-0 py-4 md:p-4">
                <Routes>
                <Route path="/login" element={<LoginForm onLogin={handleLogin} />} />
                <Route path="/signup" element={<SignupForm onSignup={handleLogin} />} />
                <Route path="/request-password-reset" element={<RequestPasswordResetForm />} />
                <Route path="/reset-password/:token" element={<ResetPasswordForm />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/profile" element={
                  <RequireAuth>
                    <ProfilePage />
                  </RequireAuth>
                } />
                <Route path="/usage" element={
                  <RequireAuth>
                    <UsageDashboard />
                  </RequireAuth>
                } />
                {/* Subscription Routes */}
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/subscription" element={
                  <RequireAuth>
                    <SubscriptionManagement />
                  </RequireAuth>
                } />
                <Route path="/subscription/success" element={
                  <RequireAuth>
                    <SubscriptionSuccessPage />
                  </RequireAuth>
                } />
                <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
                <Route path="/affiliate" element={
                  <RequireAuth>
                    <AffiliatePage />
                  </RequireAuth>
                } />
                <Route path="/" element={
                  <RequireAuth>
                    <ScreenerResults />
                  </RequireAuth>
                } />
                <Route path="/watchlist" element={
                  <RequireAuth>
                    <Watchlist />
                  </RequireAuth>
                } />
                <Route path="/stock-analyzer" element={
                  <RequireAuth>
                    <FeatureGate feature="stock_analyzer">
                      <StockAnalyzerDashboard />
                    </FeatureGate>
                  </RequireAuth>
                } />
                <Route path="/admin-tests" element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminTestDashboard />
                    </RequireAdmin>
                  </RequireAuth>
                } />
                <Route path="/research" element={
                  <RequireAuth>
                    <FeatureGate feature="research">
                      <ResearchDashboard />
                    </FeatureGate>
                  </RequireAuth>
                } />
                <Route path="/stock-analyzer/scoring-builder" element={
                  <RequireAuth>
                    <FeatureGate feature="scoring_builder">
                      <ScoringBuilderPage />
                    </FeatureGate>
                  </RequireAuth>
                } />
                <Route path="/dashboard-builder" element={
                  <RequireAuth>
                    <FeatureGate feature="dashboard_builder">
                      <DashboardBuilder />
                    </FeatureGate>
                  </RequireAuth>
                } />
                <Route path="/dashboard/:ticker" element={
                  <RequireAuth>
                    <TickerDashboard />
                  </RequireAuth>
                } />
                <Route path="/options-dashboard/:ticker/:optionType/:strike/:expiration" element={
                  <RequireAuth>
                    <OptionsDashboard />
                  </RequireAuth>
                } />
                <Route path="/test-macd" element={
                  <RequireAuth>
                    <TestMACDChart />
                  </RequireAuth>
                } />
                </Routes>
              </div>
            </div>
          </Router>
        </LastClickedTickerProvider>
      </CustomIndicatorProvider>
      </TutorialVideoProvider>
    </UserProvider>
  );
}

export default App;
